'use client';

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { AnimatePresence, motion, type Variants } from 'motion/react';
import { useLocale, useTranslations } from 'next-intl';
import { RiArrowDownSLine, RiCheckLine, RiGlobalLine } from '@remixicon/react';
import { LOCALE_META, isLocale } from '@mentorivo/shared/locales';

import { useDirection } from '@/i18n/direction';
import { Link, usePathname } from '@/i18n/navigation';
import { routing } from '@/i18n/routing';
import { dur, ease, spring, springFade } from '@/lib/motion';
import './language-menu.css';

/**
 * DİL MENÜSÜ — 13 dil (29 Eyl 2026).
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden bölümlü düğme değil
 * ═══════════════════════════════════════════════════════════════════════════
 * Footer'da iki dil varken `EN | TR` bölümlü düğmesi doğruydu: iki seçenek
 * tek bakışta okunuyor ve seçim tek dokunuş. On üç dilde aynı şerit 390px'te
 * üç satıra kırılıyor ve "hangisi etkin" sorusunu gürültüye gömüyordu. Menü
 * etkin dili ADIYLA gösteriyor ("Deutsch") ve listeyi ancak istenince açıyor.
 *
 * ---------------------------------------------------------------------------
 * Maddeler gerçek bağlantı
 * ---------------------------------------------------------------------------
 * `<button>` + `router.replace` değil `<a hrefLang lang>`: JavaScript gelmeden
 * de çalışıyor, arama motoru dil sürümlerini görüyor, orta tıkla yeni sekmede
 * açılabiliyor. `href` dil öneksiz MEVCUT yol — öneki `Link` koyuyor, yani
 * /pricing'de dil değiştirmek sizi anasayfaya atmıyor.
 *
 * next-intl'in `Link`i `locale` verilince tıklamada `NEXT_LOCALE` çerezini de
 * yazıyor: seçim bir dahaki gelişte `Accept-Language`in önüne geçiyor. Cihaz
 * dili yalnız HİÇ seçim yapmamış ziyaretçinin dilini belirliyor.
 *
 * ---------------------------------------------------------------------------
 * Kalıp: açılır gezinme (disclosure), menü değil
 * ---------------------------------------------------------------------------
 * `role="menu"` eylem listesi içindir ve ekran okuyucuya "bağlantı" yerine
 * "menü öğesi" okutur. Dil listesi bir gezinme; WAI-ARIA'nın önerdiği kalıp
 * `aria-expanded` taşıyan bir düğme + sıradan bir bağlantı listesi. Ok
 * tuşları yine çalışıyor (liste uzun) ama Tab doğal sırayla ilerliyor.
 *
 * Konum: `placement="top"` footer için (sayfanın dibinde aşağı açılamaz),
 * `"bottom"` üst çubuklar için. Panel tetikleyicinin SATIR SONU kenarına
 * hizalı (`inset-inline-end`) — sağdan sola dilde kendiliğinden sol kenar.
 */

type Placement = 'top' | 'bottom';

export function LanguageMenu({
  placement = 'top',
  className,
}: {
  placement?: Placement;
  /** Kabın renk belirteçlerini (`--lm-*`) ezmek için. */
  className?: string;
}) {
  const t = useTranslations('languageMenu');
  const locale = useLocale();
  const pathname = usePathname();
  const direction = useDirection();
  const panelId = useId();

  const [open, setOpen] = useState(false);
  /** Tıklanan dil — sayfa gelmeden etkin gösteriliyor, yoksa arayüz cevapsız kalıyor. */
  const [pending, setPending] = useState<string | null>(null);
  /** Kayan vurgunun durduğu madde (fare ya da klavye odağı). */
  const [hot, setHot] = useState<string | null>(null);

  const rootRef = useRef<HTMLElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /** Paneli görüş alanına sokan yatay kaydırma (px) — aşağıdaki ölçüm. */
  const [shift, setShift] = useState(0);
  const itemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  const shown = pending ?? locale;
  const current = LOCALE_META[isLocale(shown) ? shown : 'en'];

  const close = useCallback((focusTrigger: boolean) => {
    setOpen(false);
    setHot(null);
    if (focusTrigger) triggerRef.current?.focus();
  }, []);

  /* Dışarı tıklayınca ve odak menüden çıkınca kapan. `pointerdown`: tıklama
     tamamlanmadan kapanıyor, yoksa altta kalan bağlantıya giden tık panelin
     kapanışıyla yarışıyordu. */
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    const onFocus = (event: FocusEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) close(false);
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('focusin', onFocus);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('focusin', onFocus);
    };
  }, [open, close]);

  /* Panel tetikleyicinin satır sonu kenarına hizalı. Tetikleyici ekranın
     başına yakınsa (mobilde footer satırı alt alta dizilince) 340px'lik
     panel ekranın dışına taşar ve sayfa yatay kaydırma kazanırdı (DESIGN.md
     §2). Açılışta bir kez ölçülüp 16px'lik kenar boşluğuna itiliyor.
     `translate` ÖZELLİĞİ — Motion'ın `transform`u (y, ölçek) ile üst üste
     biniyor, onu ezmiyor. */
  useLayoutEffect(() => {
    if (!open) {
      setShift(0);
      return;
    }
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    const gutter = 16;
    const viewport = document.documentElement.clientWidth;
    if (rect.left < gutter) setShift(gutter - rect.left);
    else if (rect.right > viewport - gutter) setShift(viewport - gutter - rect.right);
  }, [open]);

  // Gezinme bitince bekleyen seçim gerçek dile dönüşmüş olur.
  useEffect(() => {
    setPending(null);
  }, [locale]);

  const focusItem = (index: number) => {
    const count = routing.locales.length;
    const next = (index + count) % count;
    itemRefs.current[next]?.focus();
  };

  const openAndFocus = (index: number) => {
    setOpen(true);
    // Panel bir sonraki karede DOM'da; odak ondan sonra.
    requestAnimationFrame(() => focusItem(index));
  };

  const onTriggerKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openAndFocus(Math.max(0, routing.locales.findIndex((code) => code === locale)));
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      close(true);
    }
  };

  /* Liste iki sütunlu ızgara ama okuma sırası düz: ↓/→ sonraki, ↑/← önceki.
     Sağdan sola dilde yatay oklar ters — "sonraki" orada solda. */
  const onListKey = (event: KeyboardEvent<HTMLUListElement>) => {
    const index = itemRefs.current.findIndex((node) => node === document.activeElement);
    const forward = direction === 'rtl' ? 'ArrowLeft' : 'ArrowRight';
    const backward = direction === 'rtl' ? 'ArrowRight' : 'ArrowLeft';

    if (event.key === 'ArrowDown' || event.key === forward) focusItem(index + 1);
    else if (event.key === 'ArrowUp' || event.key === backward) focusItem(index - 1);
    else if (event.key === 'Home') focusItem(0);
    else if (event.key === 'End') focusItem(routing.locales.length - 1);
    else if (event.key === 'Escape') close(true);
    else return;
    event.preventDefault();
  };

  const rise = placement === 'top' ? 1 : -1;
  const panel: Variants = {
    hidden: { opacity: 0, y: 6 * rise, scale: 0.97 },
    show: { opacity: 1, y: 0, scale: 1, transition: springFade(spring.snap) },
    exit: { opacity: 0, y: 4 * rise, scale: 0.985, transition: { duration: dur.instant, ease: ease.in } },
  };

  return (
    <nav
      ref={rootRef}
      className={`lang-menu${className ? ` ${className}` : ''}`}
      data-placement={placement}
      aria-label={t('label')}
    >
      <button
        ref={triggerRef}
        type="button"
        className="lang-menu-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => (open ? close(false) : setOpen(true))}
        onKeyDown={onTriggerKey}
      >
        <RiGlobalLine className="lang-menu-globe" size={16} aria-hidden="true" />
        {/* Ekran okuyucu "Dil: Deutsch, düğme, daraltılmış" duyuyor. */}
        <span className="lang-menu-sr">{t('current')}</span>
        <span className="lang-menu-current" lang={current.code}>
          {current.nativeName}
        </span>
        <motion.span
          className="lang-menu-caret"
          aria-hidden="true"
          animate={{ rotate: open === (placement === 'bottom') ? 180 : 0 }}
          transition={spring.snap}
        >
          <RiArrowDownSLine size={16} />
        </motion.span>
      </button>

      <AnimatePresence>
        {open ? (
          <motion.div
            key="panel"
            ref={panelRef}
            id={panelId}
            className="lang-menu-panel"
            style={shift ? { translate: `${shift}px 0` } : undefined}
            variants={panel}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            <ul className="lang-menu-list" onKeyDown={onListKey} onPointerLeave={() => setHot(null)}>
              {routing.locales.map((code, index) => {
                const meta = LOCALE_META[code];
                const active = shown === code;

                return (
                  <motion.li
                    key={code}
                    className="lang-menu-row"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1, transition: { delay: index * 0.015, duration: dur.fast } }}
                  >
                    <Link
                      ref={(node: HTMLAnchorElement | null) => {
                        itemRefs.current[index] = node;
                      }}
                      href={pathname}
                      locale={code}
                      hrefLang={code}
                      lang={code}
                      dir={meta.dir}
                      className="lang-menu-link"
                      data-active={active ? 'true' : 'false'}
                      aria-current={locale === code ? 'true' : undefined}
                      onPointerEnter={() => setHot(code)}
                      onFocus={() => setHot(code)}
                      onClick={() => {
                        setPending(code);
                        close(false);
                      }}
                    >
                      {/* Vurgu tek parça: `layoutId` ile maddeden maddeye
                          KAYIYOR — seçicideki (`DashSelect`) şeritle aynı jest. */}
                      {hot === code ? (
                        <motion.span
                          className="lang-menu-hot"
                          layoutId={`${panelId}-hot`}
                          transition={spring.snap}
                          aria-hidden="true"
                        />
                      ) : null}
                      <span className="lang-menu-name">{meta.nativeName}</span>
                      {active ? (
                        <RiCheckLine className="lang-menu-check" size={16} aria-hidden="true" />
                      ) : (
                        <span className="lang-menu-code" aria-hidden="true">
                          {code}
                        </span>
                      )}
                    </Link>
                  </motion.li>
                );
              })}
            </ul>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </nav>
  );
}
